import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { fetchSsoDiscovery } from "../services/studio/api";
import { logError, scrubMessage } from "../telemetry";
import styles from "./MsalProvider.module.css";

/* ── Context ─────────────────────────────────── */

interface AuthCapabilities {
  /** Whether Microsoft Graph is available (Entra only). Non-Entra providers
   *  authenticate but have no Graph token — Graph features must gate on this. */
  graph: boolean;
}

interface AuthContext {
  ready: boolean;
  /** The resolved SSO provider (e.g. "AzureAd", "Okta", "Auth0", "Custom"). */
  provider: string;
  capabilities: AuthCapabilities;
  /**
   * Office host channel (outlook/word/powerpoint) this pane resolved its SSO
   * config for. The BFF refresh call needs it so the backend selects the same
   * discovery config.
   */
  channel: string | null;
  /**
   * Minutes of no task-pane use before sign-out, from the Session Persistence
   * setting on the Studio SSO page; null when the workspace has not set one
   * and the auth state keeps its built-in default. Part of the bootstrap, so it
   * is in place before the auth state first renders and runs its reopen gate.
   */
  idleTimeoutMinutes: number | null;
}

/**
 * Everything the bootstrap resolves, published as a single value so the app is
 * never rendered against a half-resolved auth config.
 */
type AuthBootstrap = Omit<AuthContext, "ready">;

/** Context value while the bootstrap is still running (and the default). */
const UNRESOLVED_AUTH_CONTEXT: AuthContext = {
  ready: false,
  provider: "",
  capabilities: { graph: true },
  channel: null,
  idleTimeoutMinutes: null,
};

const AuthCtx = createContext<AuthContext>(UNRESOLVED_AUTH_CONTEXT);

export function useAuthContext(): AuthContext {
  return useContext(AuthCtx);
}

/* ── Helpers ──────────────────────────────────── */

/** Azure AD (or unknown, which is treated as Entra) is the only provider that
 *  has a Microsoft Graph audience to mint tokens for. */
function isEntraProvider(provider: string): boolean {
  return provider === "" || provider.toLowerCase() === "azuread";
}

interface ResolvedAuthConfig {
  clientId: string;
  authority: string;
  provider: string;
  idleTimeoutMinutes: number | null;
}

/**
 * Resolve the add-in's SSO config from the Studio discovery endpoint — the sole
 * source of truth. There is intentionally NO env fallback: if discovery fails
 * (or the host/channel is unknown), the config is left empty and the provider
 * surfaces a blocking error rather than silently running on stale env config.
 */
async function resolveAuthConfig(
  channel: string | null | undefined,
): Promise<ResolvedAuthConfig> {
  const empty: ResolvedAuthConfig = { clientId: "", authority: "", provider: "", idleTimeoutMinutes: null };

  if (!channel) return empty;

  try {
    const disc = await fetchSsoDiscovery(channel);
    return {
      clientId: disc.clientId,
      authority: disc.authority,
      provider: disc.provider,
      idleTimeoutMinutes: disc.idleTimeoutMinutes,
    };
  } catch (err) {
    logError("error.sso.discovery", {
      type: err instanceof Error ? err.name : "SsoDiscoveryError",
      scrubbedMessage: scrubMessage(err instanceof Error ? err.message : String(err)),
    });
    return empty;
  }
}

/* ── Provider ─────────────────────────────────── */

interface Props {
  readonly children: ReactNode;
  /**
   * Office host channel (outlook/word/powerpoint) used to fetch the SSO
   * discovery config. Null when the host is unknown, in which case the
   * bootstrap surfaces a configuration error.
   */
  readonly channel?: string | null;
}

/**
 * Provides auth context to the add-in, bootstrapped from the Studio SSO
 * discovery endpoint (AIVA-6162).
 *
 * The task pane runs **no browser token client** — no MSAL, no oidc-client-ts
 * (V-08). Interactive sign-in is an Office dialog that posts its authorization
 * code to the backend, and silent refresh is a cookie-credentialed fetch, so
 * the only thing this provider has to resolve is *which* IdP the tenant uses
 * and what that implies for capabilities. (The file name is historical — it
 * predates the removal of MSAL from the pane.)
 *
 * `capabilities.graph` is false for Okta / Auth0 / Custom, so Graph-dependent
 * features gate off: those providers have no Microsoft Graph audience and the
 * backend returns a null Graph token for them.
 *
 * The Studio discovery endpoint is the sole source of config — there is no env
 * fallback. If discovery doesn't resolve, a blocking configuration error is shown.
 */
export function MsalAuthProvider({ children, channel }: Readonly<Props>) {
  // The bootstrap result lands as ONE state transition, so the tree shape is
  // settled before children ever mount. See `bootstrap == null` in the render
  // below: children that mount against a half-resolved config used to be
  // remounted when the rest arrived, which discarded a sign-in completed
  // mid-bootstrap and sent the user back to the sign-in screen.
  const [bootstrap, setBootstrap] = useState<AuthBootstrap | null>(null);
  const [initError, setInitError] = useState<string | null>(null);

  const didInit = useRef(false);
  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;

    void (async () => {
      const { clientId, authority, provider: resolvedProvider, idleTimeoutMinutes } =
        await resolveAuthConfig(channel);

      if (!clientId || !authority) {
        logError(
          "app.auth.failure",
          { type: "SsoConfigUnavailable", scrubbedMessage: "sso_config_unavailable" },
          { reason: "sso_config_unavailable" },
        );
        setInitError(
          "Single sign-on configuration is unavailable. Please contact your administrator.",
        );
        return;
      }

      // Publishing the bootstrap marks us ready, so the app proceeds to the
      // sign-in screen. There is no token client to build here for any
      // provider: the backend holds the refresh token and mints every
      // audience, so sign-in is the dialog and refresh is a fetch.
      setBootstrap({
        provider: resolvedProvider,
        capabilities: { graph: isEntraProvider(resolvedProvider) },
        channel: channel ?? null,
        idleTimeoutMinutes,
      });
    })();
  }, [channel]);

  const ctx = useMemo<AuthContext>(
    () => (bootstrap ? { ready: true, ...bootstrap } : UNRESOLVED_AUTH_CONTEXT),
    [bootstrap],
  );

  if (initError) {
    return (
      <div role="alert" className={styles.errorBox}>
        <p className={styles.errorTitle}>Configuration Error</p>
        <p>{initError}</p>
      </div>
    );
  }

  // Hold the app back until the bootstrap has settled. Rendering children
  // earlier put a live "Sign in with SSO" button on screen before we knew
  // which provider this tenant uses, so an early click took a path chosen
  // against an unresolved config.
  if (!bootstrap) {
    return <output className={styles.loadingBox}>Loading AiVA…</output>;
  }

  return (
    <AuthCtx.Provider value={ctx}>
      {children}
    </AuthCtx.Provider>
  );
}
